/**
 * DataHubScreen - Data source selection screen for the onboarding flow
 * Allows users to select which data sources they want to connect
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../contexts/OnboardingContext';
import ClientSelector from '../kyc/ClientSelector';
import { UserPlus } from 'lucide-react';

interface DataHubScreenProps {
  onStartWizard: () => void;
}

const DataHubScreen: React.FC<DataHubScreenProps> = ({ onStartWizard }) => {
  const { 
    setCurrentScreen, 
    selectedSources, 
    setSelectedSources,
    connectedSources,
    dataSources,
    refreshConnectedSources
  } = useOnboarding();
  
  const navigate = useNavigate();
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [showClientSelector, setShowClientSelector] = useState(false);

  // Refresh connected sources when DataHubScreen loads
  useEffect(() => {
    console.log('DataHubScreen mounted, refreshing connected sources...');
    refreshConnectedSources();
  }, [refreshConnectedSources]);

  const toggleSource = (sourceId: string) => {
    const source = dataSources.find(s => s.id === sourceId);
    if (source?.disabled) return;

    if (selectedSources.includes(sourceId)) {
      setSelectedSources(selectedSources.filter(s => s !== sourceId));
    } else {
      setSelectedSources([...selectedSources, sourceId]);
    }
  };

  const handleContinue = () => {
    // If we have connected sources but no selected sources, go directly to review
    if (connectedSources.length > 0 && selectedSources.length === 0) {
      setCurrentScreen('quick-review');
    } else {
      onStartWizard();
    }
  };

  const handleStartKYC = () => {
    setShowClientSelector(true);
  };

  const handleClientSelected = (client: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    type?: string
  }) => {
    setShowClientSelector(false);
    navigate(`/clients/${client.id}/kyc-workspace`);
  };

  return (
    <div className="min-h-screen bg-kielis-glacier">
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="container mx-auto px-5">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-kielis-navy font-semibold">Connect Your Data Sources</h3>
              <p className="text-gray-600 text-sm">Select the sources you want to connect now. You can add more later.</p>
            </div>
            <div className="flex items-center gap-4">
              {/* Add New Customer Button */}
              <button
                onClick={handleStartKYC}
                className="flex items-center gap-2 px-4 py-2 bg-kielis-gold text-kielis-navy font-medium rounded-lg hover:bg-opacity-90 transition-all shadow-sm"
              >
                <UserPlus className="w-4 h-4" />
                KYC Registration
              </button>

              <span className="text-gray-600 text-sm border-l pl-4 border-gray-300">
                {selectedSources.length > 0 ? `${selectedSources.length} selected` : 'Nothing selected'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-5 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {dataSources.map((source) => (
            <div
              key={source.id}
              className={`
                relative bg-white border-2 rounded-xl p-6 cursor-pointer transition-all group
                ${source.disabled 
                  ? 'opacity-50 cursor-not-allowed border-gray-200' 
                  : selectedSources.includes(source.id)
                    ? 'border-kielis-gold bg-kielis-glacier'
                    : connectedSources.includes(source.id)
                      ? 'border-kielis-success bg-green-50'
                      : 'border-gray-200 hover:border-kielis-navy hover:transform hover:-translate-y-1'
                }
              `}
              onClick={() => toggleSource(source.id)}
              onMouseEnter={() => !source.disabled && setShowTooltip(source.id)}
              onMouseLeave={() => setShowTooltip(null)}
              tabIndex={source.disabled ? -1 : 0}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !source.disabled) {
                  e.preventDefault();
                  toggleSource(source.id);
                }
              }}
            >
              {!source.disabled && selectedSources.includes(source.id) && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-kielis-gold rounded-full flex items-center justify-center text-white text-sm font-bold">
                  ✓
                </div>
              )}
              {connectedSources.includes(source.id) && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-kielis-success rounded-full flex items-center justify-center text-white text-sm font-bold">
                  ✓
                </div>
              )}
              {source.disabled && (
                <div className="absolute top-4 right-4 text-gray-400">⏳</div>
              )}
              
              <div className={`w-12 h-12 ${source.bgColor} text-white rounded-lg flex items-center justify-center text-2xl mb-4`}>
                {source.icon}
              </div>
              
              <h4 className={`font-medium mb-2 ${source.disabled ? 'text-gray-500' : 'text-kielis-navy'}`}>
                {source.title}
              </h4>
              
              <p className={`text-sm mb-4 ${source.disabled ? 'text-gray-400' : 'text-gray-600'} line-clamp-2`}>
                {source.description}
              </p>
              
              <div className={`text-xs ${source.disabled ? 'text-gray-400' : 'text-gray-600'}`}>
                {source.supports}
              </div>
              
              {connectedSources.includes(source.id) && (
                <div className="mt-3 pt-3 border-t border-green-200">
                  <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                    <span className="text-green-600">✓</span>
                    Already Connected
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    Click to reconfigure or manage
                  </p>
                </div>
              )}

              {/* Tooltip */}
              {showTooltip === source.id && source.tooltip && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-kielis-navy text-white text-sm rounded-md whitespace-nowrap z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {source.tooltip}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-kielis-navy"></div>
                </div>
              )}

              {source.disabled && source.comingSoon && (
                <button 
                  className="mt-3 bg-gray-200 text-gray-600 px-3 py-1 rounded text-xs animate-pulse"
                  onClick={(e) => {
                    e.stopPropagation();
                    alert(`📧 Early Access Notification\n\nThanks for your interest! We'll notify you when Custodian API integration becomes available.\n\nExpected: ${source.comingSoon}`);
                  }}
                >
                  Notify me
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="text-center">
          <button 
            className="text-gray-600 underline mr-8"
            onClick={() => setCurrentScreen('welcome')}
          >
            ← Back
          </button>
          <button 
            className={`px-8 py-3 rounded-lg font-medium transition-all ${
              selectedSources.length > 0 || connectedSources.length > 0
                ? 'bg-kielis-navy text-white hover:bg-opacity-90' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            disabled={selectedSources.length === 0 && connectedSources.length === 0}
            onClick={handleContinue}
          >
            Continue with Selected Sources
          </button>
          <p className="text-gray-600 text-sm mt-4">
            Add or remove sources anytime in Settings
          </p>
        </div>
      </div>

      {/* Client Selector Modal */}
      <ClientSelector
        isOpen={showClientSelector}
        onClose={() => setShowClientSelector(false)}
        onSelectClient={handleClientSelected}
      />
    </div>
  );
};

export default DataHubScreen;